import type { ReactNode } from "react";

type OptInCheckboxesProps = {
  enrollMastermind: boolean;
  subscribeNewsletter: boolean;
  setEnrollMastermind: (value: boolean) => void;
  setSubscribeNewsletter: (value: boolean) => void;
  mastermindRowClassName?: string;
  newsletterRowClassName?: string;
  mastermindLabelClassName?: string;
  newsletterLabelClassName?: string;
};

function CheckIcon({ checked }: { checked: boolean }) {
  return (
    <svg
      className={`h-2.5 w-2.5 transition-opacity ${checked ? "text-black opacity-100" : "text-white opacity-0"}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

type OptInRowProps = {
  id: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  rowClassName?: string;
  labelClassName?: string;
  children: ReactNode;
};

function OptInRow({
  id,
  checked,
  onChange,
  rowClassName = "",
  labelClassName = "text-sm text-secondary leading-snug",
  children,
}: OptInRowProps) {
  return (
    <label
      htmlFor={id}
      className={`flex cursor-pointer items-start gap-3 ${rowClassName}`.trim()}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      <span
        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors peer-focus-visible:ring-1 peer-focus-visible:ring-white/40 ${
          checked
            ? "border-white bg-white"
            : "border-white/30 bg-black"
        }`}
        aria-hidden
      >
        <CheckIcon checked={checked} />
      </span>
      <span className={labelClassName}>{children}</span>
    </label>
  );
}

/**
 * Mastermind + newsletter opt-ins (independent checkboxes — both can stay checked).
 * Custom control avoids iOS/Safari showing native + styled double checkmarks.
 */
export default function OptInCheckboxes({
  enrollMastermind,
  subscribeNewsletter,
  setEnrollMastermind,
  setSubscribeNewsletter,
  mastermindRowClassName,
  newsletterRowClassName,
  mastermindLabelClassName,
  newsletterLabelClassName,
}: OptInCheckboxesProps) {
  return (
    <div className="flex flex-col gap-3">
      <OptInRow
        id="opt-in-mastermind"
        checked={enrollMastermind}
        onChange={setEnrollMastermind}
        rowClassName={mastermindRowClassName}
        labelClassName={mastermindLabelClassName}
      >
        I also want to join the{" "}
        <span className="font-medium text-white">
          2-Day AI Mastermind by Outskill
        </span>{" "}
        for free
      </OptInRow>

      <OptInRow
        id="opt-in-newsletter"
        checked={subscribeNewsletter}
        onChange={setSubscribeNewsletter}
        rowClassName={newsletterRowClassName}
        labelClassName={newsletterLabelClassName}
      >
        Also subscribe me to the newsletter
        <br />
        <span className="text-xs text-secondary/70">
          Staying Ahead with AI Updates
        </span>
      </OptInRow>
    </div>
  );
}
